import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';

import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';

import CreateBackflowJobGroupModal from '../components/CreateBackflowJobGroupModal';
import DeleteConfirmationModal from '../../../sharedModules/layout/DeleteConfirmationModal';
import DataTable from '../../../sharedModules/dataTable';
import BackflowJobsFilter from '../components/BackflowJobsFilter';
import PageHeader from '../../../sharedModules/layout/PageHeader';

import { BackflowJobGroup } from '../../model';
import { backflowJobGroupsColumns } from '../constants/backflowListColumns';

import { AuthenticationContext } from '../../../homeModules/auth/contexts/AuthenticationContext';
import { DeleteConfirmationModalContext } from '../../../sharedModules/contexts/DeleteConfirmationModalContext';

import axios from '../../../sharedModules/utils/axios.utils';

const defaultFilterValues = {
  status_in: ['in_progress', 'not_started', 'overdue'],
  company_name_eq: 'all',
};

const BackflowJobGroupsListPage: React.FunctionComponent = () => {
  const history = useHistory();

  const { user } = useContext(AuthenticationContext);

  const { setModelLabel, setDeleteConfirmationModalOpen, setRecordToDelete, setDeleteURL } =
    useContext(DeleteConfirmationModalContext);

  const [createJobGroupModalOpen, setCreateJobGroupModalOpen] = useState<boolean>(false);
  const [backflowJobGroups, setBackflowJobGroups] = useState<BackflowJobGroup[]>([]);
  const [filterValues, setFilterValues] = useState<any>({});

  const fetchBackflowJobGroups = async (fetchFilterValues = filterValues) => {
    try {
      const response = await axios.get<string, any>('/backflows/backflow_job_groups', {
        params: {
          'ransack[backflow_dates_scheduled_date_gteq]': fetchFilterValues.backflow_dates_scheduled_date_gteq,
          'ransack[backflow_dates_scheduled_date_lteq]': fetchFilterValues.backflow_dates_scheduled_date_lteq,
          'ransack[status_in]': fetchFilterValues.status_in?.length > 0 ? fetchFilterValues.status_in : undefined,
          'ransack[company_name_eq]':
            fetchFilterValues.company_name_eq !== 'all' ? fetchFilterValues.company_name_eq : undefined,
        },
      });
      setBackflowJobGroups(response?.data?.result);
    } catch (error) {
      console.error(error);
    }
  };

  const conditionalRowStyles = [
    {
      when: (row) => row.status?.toLowerCase() === 'overdue' || row.status?.toLowerCase() === 'closed no log',
      style: {
        backgroundColor: 'rgba(242, 38, 19, 0.9)',
        color: 'white',
        '&:hover': {
          cursor: 'not-allowed',
        },
      },
    },
  ];

  useEffect(() => {
    if (user && Object.keys(defaultFilterValues).length > 0) {
      setFilterValues(defaultFilterValues);
      fetchBackflowJobGroups(defaultFilterValues);
    }
  }, [user, defaultFilterValues]);

  const handleDelete = (backflowJobGroup) => {
    setModelLabel('Work Order');
    setDeleteConfirmationModalOpen(true);
    setRecordToDelete(backflowJobGroup);
    setDeleteURL('backflows/backflow_job_groups/');
  };

  const columns = backflowJobGroupsColumns(handleDelete);

  return (
    <Grid container>
      <CreateBackflowJobGroupModal
        fetchBackflowJobGroups={fetchBackflowJobGroups}
        createJobGroupModalOpen={createJobGroupModalOpen}
        setCreateJobGroupModalOpen={setCreateJobGroupModalOpen}
      />
      <DeleteConfirmationModal afterDelete={fetchBackflowJobGroups} />
      <Grid item xs={12}>
        <PageHeader
          mainTitle="Backflow Testing"
          subTitle="Upcoming Tests"
          button={
            <Button variant="contained" color="primary" onClick={() => setCreateJobGroupModalOpen(true)}>
              Import Work Order
            </Button>
          }
        />
      </Grid>
      <Grid item xs={12} style={{ marginTop: 20, marginBottom: 20 }}>
        <BackflowJobsFilter
          filterValues={filterValues}
          setFilterValues={setFilterValues}
          onFilter={(filterVals) => fetchBackflowJobGroups(filterVals)}
        />
      </Grid>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <DataTable
              title=""
              columns={columns}
              data={backflowJobGroups}
              onRowClicked={(row: any) => history.push(`/backflow_tests/backflow_job_groups/${row.id}`)}
              conditionalRowStyles={conditionalRowStyles}
              dense
              highlightOnHover
              pointerOnHover
              pagination
              paginationPerPage={25}
              paginationRowsPerPageOptions={[25, 50, 100]}
              noHeader
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default BackflowJobGroupsListPage;
